import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import { usePreferences } from 'hooks/usePreferences';
import { formatDateWithPreferences } from 'utils/dateFormatUtils';
import { formatAmountForDisplayWithPreferences } from 'utils/index';

const PaymentMethodsReport = ({ data }) => {
  const { preferences } = usePreferences();

  if (!data || !data.summary) {
    return (
      <Box>
        <Typography variant="h5" gutterBottom>Payment Methods Report</Typography>
        <Typography color="textSecondary">No data available for this report.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Payment Methods Report
      </Typography>
      <Typography variant="subtitle1" color="textSecondary" gutterBottom>
        Date range: {formatDateWithPreferences(data.period?.startDate, preferences)} to {formatDateWithPreferences(data.period?.endDate, preferences)}
      </Typography>
      {data.location && (
        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
          Location: {data.location}
        </Typography>
      )}

      <Grid container spacing={2} sx={{ mb: 3, mt: 1 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Gross Received</Typography>
              <Typography variant="h5">{formatAmountForDisplayWithPreferences(data.summary.grossReceived || 0, preferences)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Refunds/Reversals</Typography>
              <Typography variant="h5" sx={{ color: (data.summary.refundsReversals || 0) > 0 ? 'error.main' : 'text.primary' }}>
                {formatAmountForDisplayWithPreferences(data.summary.refundsReversals || 0, preferences)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Net Received</Typography>
              <Typography variant="h5" sx={{ color: (data.summary.netReceived || 0) >= 0 ? 'success.main' : 'error.main' }}>
                {formatAmountForDisplayWithPreferences(data.summary.netReceived || 0, preferences)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Transactions</Typography>
              <Typography variant="h5">{data.summary.totalTransactions || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom>Payment Method Breakdown</Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Payment Method</TableCell>
              <TableCell align="right">Transactions</TableCell>
              <TableCell align="right">Gross</TableCell>
              <TableCell align="right">Refunds/Reversals</TableCell>
              <TableCell align="right">Net</TableCell>
              <TableCell align="right">Gross %</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data.breakdown || []).map((method) => (
              <TableRow key={method.payment_method}>
                <TableCell>{method.payment_method}</TableCell>
                <TableCell align="right">{method.transaction_count}</TableCell>
                <TableCell align="right">{formatAmountForDisplayWithPreferences(method.gross_amount || 0, preferences)}</TableCell>
                <TableCell align="right">{formatAmountForDisplayWithPreferences(method.refunds_reversals || 0, preferences)}</TableCell>
                <TableCell align="right" sx={{ color: (method.net_amount || 0) >= 0 ? 'text.primary' : 'error.main' }}>
                  {formatAmountForDisplayWithPreferences(method.net_amount || 0, preferences)}
                </TableCell>
                <TableCell align="right">{method.percentage}%</TableCell>
              </TableRow>
            ))}
            {(!data.breakdown || data.breakdown.length === 0) && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="textSecondary">No payments found for the selected filters</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PaymentMethodsReport;
